/*
  "Choose options" drawer (snippets/variant-drawer.liquid).
  Product card buttons for products with variants carry [data-variant-drawer] and
  data-product-handle. Clicking one opens the drawer and loads the "product-choose"
  section for that product: photo strip, title, price, and the product page's option
  picker, quantity and add to cart (theme <product-form>).
  The drawer closes once the theme reports a successful add.
*/
(() => {
  if (customElements.get('m-variant-drawer')) return;

  class VariantDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.m-vdrawer__panel');
      this.content = this.querySelector('[data-vd-content]');
      this.request = 0;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-vd-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      document.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('[data-variant-drawer]');
          if (!trigger || !trigger.dataset.productHandle || this.contains(trigger)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.open(trigger);
        },
        true
      );

      const subscribe = () => {
        if (window.DeliEvents && window.DeliTheme && DeliTheme.pubSubEvents) {
          // Show the selected variant's photo in the strip.
          DeliEvents.subscribe(DeliTheme.pubSubEvents.variantChange, (event) => {
            const data = event && event.data;
            if (!this.isOpen || !data || data.sectionId !== 'product-choose') return;
            const media = data.variant && data.variant.featured_media;
            const gallery = this.querySelector('[data-pchoose-gallery]');
            const slide = media && gallery && gallery.querySelector(`[data-media-id="${media.id}"]`);
            if (slide) gallery.scrollTo({ left: slide.offsetLeft - gallery.offsetLeft, behavior: 'smooth' });
          });
          DeliEvents.subscribe(DeliTheme.pubSubEvents.cartUpdate, (event) => {
            if (!this.isOpen || !event || !event.cart || event.cart.errors) return;
            this.close(false);
          });
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', subscribe);
      } else {
        subscribe();
      }
    }

    open(trigger) {
      this.trigger = trigger;
      const handle = trigger.dataset.productHandle;
      const base = (window.DeliSettings && DeliSettings.base_url) || `${window.location.origin}/`;
      const request = ++this.request;

      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.show();

      fetchSection('product-choose', { url: `${base}products/${encodeURIComponent(handle)}` })
        .then((html) => {
          if (request !== this.request || !this.isOpen) return;
          const view = html.querySelector('#ProductChoose-content');
          if (!view) throw new Error('Choose content not found');
          // Keep "View full details" with the sticky Add to cart footer.
          const details = view.querySelector('.m-pchoose__details');
          const buy = view.querySelector('.main-product__block-buy_buttons');
          if (details && buy) buy.appendChild(details);

          this.content.appendChild(view);
          this.runScripts(view);
          this.classList.remove('is-loading');
        })
        .catch(() => {
          // Fall back to the product page if the product can't be loaded.
          window.location.href = trigger.dataset.productUrl || trigger.getAttribute('href') || `${base}products/${handle}`;
        });
    }

    // Scripts inserted with innerHTML don't run; recreate them (skipping files already on the page).
    runScripts(root) {
      root.querySelectorAll('script').forEach((old) => {
        const src = old.getAttribute('src');
        if (src && Array.from(document.scripts).some((s) => s !== old && !root.contains(s) && s.getAttribute('src') === src)) {
          old.remove();
          return;
        }
        const script = document.createElement('script');
        Array.from(old.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
        script.textContent = old.textContent;
        old.replaceWith(script);
      });
    }

    show() {
      this.isOpen = true;
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('m-vdrawer-open');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.panel.focus({ preventScroll: true });
      });
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.request += 1;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('m-vdrawer-open');
      if (typeof pauseAllMedia === 'function') pauseAllMedia(this);
      // Clear after the slide-out so the next product starts fresh.
      setTimeout(() => {
        if (!this.isOpen) this.content.innerHTML = '';
      }, 450);
      if (restoreFocus && this.trigger) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define('m-variant-drawer', VariantDrawer);
})();
